import {
  Firestore,
  collection,
  CollectionReference,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
  doc,
  updateDoc,
} from "firebase/firestore/lite";
import { DbBusinessReview } from "../business/models";
import { bookingService } from "./service";
import { DbBooking } from "./models";
import { firebaseDb } from "../firebase/client-app";
import { DbVisit, DbVisitType } from "../visit/models";
import { DateTime } from "luxon";

export class BookingVisitsService {
  visitRef(firestore: Firestore): CollectionReference {
    return collection(firestore, "visits");
  }

  async getBookingsFromVisits(
    visits: DbVisit[],
    businessId: string,
    firestore: Firestore = firebaseDb,
    advanceServicesOnly: boolean = false,
  ): Promise<DbBooking[]> {
    const bookingIds: string[] = [];
    visits.forEach((v) => {
      v.services?.forEach((s) => {
        if (s?.booking_id) {
          if (advanceServicesOnly && s.type !== DbVisitType.Advance) {
            return;
          }
          bookingIds.push(s.booking_id);
        }
      });
    });
    if (bookingIds.length === 0) return [];

    const bookingsList = await bookingService.getBookingsByIds(
      businessId,
      bookingIds,
      firestore,
    );
    return bookingsList.sort((a, b) => {
      const da =
        a.booking_date.toJSDate?.() ?? new Date(a.booking_date.seconds! * 1000);
      const db =
        b.booking_date.toJSDate?.() ?? new Date(b.booking_date.seconds! * 1000);
      return (da as Date).getTime() - (db as Date).getTime();
    });
  }

  async getAllVisitsOfBusiness(
    businessId: string,
    pageSize: number = 10,
    startAfterDate?: DateTime | null,
    firestore: Firestore = firebaseDb,
  ): Promise<DbVisit[]> {
    let visitsQuery = query(
      this.visitRef(firestore),
      where("business_id", "==", businessId),
      orderBy("booking_date", "desc"),
      limit(pageSize),
    );
    if (startAfterDate) {
      visitsQuery = query(
        visitsQuery,
        startAfter(Timestamp.fromDate(startAfterDate.toJSDate())),
      );
    }
    const visitsDocs = await getDocs(visitsQuery);
    return visitsDocs.docs.map((d) => d.data() as DbVisit);
  }

  async getUserVisitsForBusiness(
    businessId: string,
    userId: string,
    lastVisit?: DbVisit | null,
    pageSize?: number | null,
    firestore: Firestore = firebaseDb,
  ): Promise<DbVisit[]> {
    let visitsRef = query(
      this.visitRef(firestore),
      where("business_id", "==", businessId),
      where("user_id", "==", userId),
      orderBy("booking_date", "desc"),
    );
    if (pageSize && pageSize > 0) {
      visitsRef = query(visitsRef, limit(pageSize));
      if (lastVisit?.booking_date) {
        visitsRef = query(visitsRef, startAfter(lastVisit.booking_date));
      }
    }
    const visitsDocs = await getDocs(visitsRef);
    return visitsDocs.docs.map((d) => d.data() as DbVisit);
  }

  async addReview(
    visitId: string,
    data: DbBusinessReview,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.visitRef(firestore), visitId), {
      review: data,
    });
  }
}

export const bookingVisitsService = new BookingVisitsService();
